"use client";

import { useLayoutEffect, useRef, type ReactNode } from "react";
import { cn } from "@/lib/utils/cn";

const HEIGHT_VARIABLE = "--sticky-tabs-bar-height";

let heightOwner: HTMLElement | null = null;

export interface StickyTabsBarProps {
  children: ReactNode;
  className?: string;
}

// Publishes its own height so content below it can stick flush underneath, the same way
// this bar sticks below DetailPageTopBar's `--detail-header-offset`.
export function StickyTabsBar({ children, className }: StickyTabsBarProps) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!ref.current) return;
    // Re-bound so the narrowing survives into the function declarations below.
    const bar: HTMLElement = ref.current;
    const root = document.documentElement;
    heightOwner = bar;

    function publish() {
      if (heightOwner !== bar) return;
      root.style.setProperty(HEIGHT_VARIABLE, `${bar.offsetHeight}px`);
    }

    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(bar);

    return () => {
      observer.disconnect();
      if (heightOwner !== bar) return;
      heightOwner = null;
      root.style.removeProperty(HEIGHT_VARIABLE);
    };
  }, []);

  return (
    <div
      ref={ref}
      className={cn(
        "sticky z-10 -mx-8 border-b border-gray-200 bg-gray-50 px-8 py-3",
        className,
      )}
      style={{ top: "var(--detail-header-offset, 0px)" }}
    >
      {children}
    </div>
  );
}
